import React, { useState } from "react";
import { toast } from "@jsq/ds/core";
import { useCustomFields } from "../../hooks/useCustomFields";
import {
  CustomFieldType,
  CustomFieldEntityType,
  CreateCustomFieldInput,
  PrefillOption,
  Section,
} from "../../types";
import CustomFieldModal from "../CustomFieldModal";

interface Props {
  show: boolean;
  onHide: () => void;
  sections?: Section[] | null;
}

export const initialValues: CreateCustomFieldInput = {
  name: "",
  type: CustomFieldType.TEXT,
  entityType: CustomFieldEntityType.ENTITY,
  options: [],
  prefillOption: PrefillOption.COUNTRY,
  sectionId: "",
};

const CreateCustomFieldModal: React.FC<Props> = ({ show, onHide, sections = null }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { createField } = useCustomFields();

  const handleSubmit = async (values: Partial<CreateCustomFieldInput>) => {
    try {
      setIsSubmitting(true);

      const fieldInput: CreateCustomFieldInput = {
        name: (values.name || "").trim(),
        description: values.description,
        type: values.type || initialValues.type,
        entityType: values.entityType || initialValues.entityType,
        options: values.options || initialValues.options,
        sectionId: values.sectionId,
      };

      await createField(fieldInput);
      toast.success("Custom field created successfully");
      onHide();
    } catch (error) {
      // Only show generic error for unexpected failures, not validation errors
      // since client-side validation should prevent submission with empty enum options
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create custom field";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CustomFieldModal<CreateCustomFieldInput>
      show={show}
      onHide={onHide}
      title="Create custom field"
      submitButtonText="Create field"
      submitButtonTestId="create-field-submit"
      isSubmitting={isSubmitting}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      isEditMode={false}
      sections={sections}
    />
  );
};

export default CreateCustomFieldModal;
